import React, { useState, useRef, useEffect } from 'react';

export const COUNTRY_CODES = [
  { code: '+91',  flag: '🇮🇳', name: 'India',          iso: 'IN' },
  { code: '+1',   flag: '🇺🇸', name: 'United States',  iso: 'US' },
  { code: '+44',  flag: '🇬🇧', name: 'United Kingdom', iso: 'GB' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia',      iso: 'AU' },
  { code: '+1',   flag: '🇨🇦', name: 'Canada',         iso: 'CA' },
  { code: '+971', flag: '🇦🇪', name: 'UAE',            iso: 'AE' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia',   iso: 'SA' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar',          iso: 'QA' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait',         iso: 'KW' },
  { code: '+968', flag: '🇴🇲', name: 'Oman',           iso: 'OM' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain',        iso: 'BH' },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia',       iso: 'MY' },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore',      iso: 'SG' },
  { code: '+64',  flag: '🇳🇿', name: 'New Zealand',    iso: 'NZ' },
  { code: '+49',  flag: '🇩🇪', name: 'Germany',        iso: 'DE' },
  { code: '+33',  flag: '🇫🇷', name: 'France',         iso: 'FR' },
  { code: '+39',  flag: '🇮🇹', name: 'Italy',          iso: 'IT' },
  { code: '+34',  flag: '🇪🇸', name: 'Spain',          iso: 'ES' },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands',    iso: 'NL' },
  { code: '+7',   flag: '🇷🇺', name: 'Russia',         iso: 'RU' },
  { code: '+86',  flag: '🇨🇳', name: 'China',          iso: 'CN' },
  { code: '+81',  flag: '🇯🇵', name: 'Japan',          iso: 'JP' },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea',    iso: 'KR' },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil',         iso: 'BR' },
  { code: '+52',  flag: '🇲🇽', name: 'Mexico',         iso: 'MX' },
  { code: '+27',  flag: '🇿🇦', name: 'South Africa',   iso: 'ZA' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria',        iso: 'NG' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya',          iso: 'KE' },
  { code: '+20',  flag: '🇪🇬', name: 'Egypt',          iso: 'EG' },
  { code: '+92',  flag: '🇵🇰', name: 'Pakistan',       iso: 'PK' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh',     iso: 'BD' },
  { code: '+94',  flag: '🇱🇰', name: 'Sri Lanka',      iso: 'LK' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal',          iso: 'NP' },
];

// Parse a full phone string like "+91 98765 43210" or "9876543210"
export function parsePhone(value) {
  if (!value) return { dialCode: '+91', number: '' };
  const match = COUNTRY_CODES.find(c => value.startsWith(c.code));
  if (match) {
    return { dialCode: match.code, number: value.slice(match.code.length).trim() };
  }
  return { dialCode: '+91', number: value };
}

// Combine dialCode + number into a single string for storage
export function formatPhone(dialCode, number) {
  const n = number.replace(/\D/g, '');
  if (!n) return '';
  return dialCode + n;
}

export default function PhoneInput({ value, onChange, placeholder, style, required }) {
  const { dialCode: initDial, number: initNum } = parsePhone(value);
  const [dialCode, setDialCode] = useState(initDial);
  const [number,   setNumber]   = useState(initNum);
  const [open,     setOpen]     = useState(false);
  const [search,   setSearch]   = useState('');
  const ref = useRef(null);

  // Sync if parent value changes
  useEffect(() => {
    const { dialCode: d, number: n } = parsePhone(value);
    setDialCode(d); setNumber(n);
  }, [value]); // eslint-disable-line

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (code) => {
    setDialCode(code);
    setOpen(false);
    setSearch('');
    onChange(formatPhone(code, number));
  };

  const handleNumber = (e) => {
    const n = e.target.value;
    setNumber(n);
    onChange(formatPhone(dialCode, n));
  };

  const selected = COUNTRY_CODES.find(c => c.code === dialCode) || COUNTRY_CODES[0];
  const filtered = COUNTRY_CODES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.includes(search)
  );

  return (
    <div ref={ref} style={{ display: 'flex', gap: 0, position: 'relative', ...style }}>
      {/* Country code picker */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '0 10px', height: 38, borderRadius: '8px 0 0 8px',
          border: '1px solid rgba(0,0,0,0.12)', borderRight: 'none',
          background: 'rgba(0,0,0,0.02)', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#374151', flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 16 }}>{selected.flag}</span>
        <span style={{ fontSize: 12 }}>{selected.code}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* Number input */}
      <input
        type="tel"
        className="crm-input"
        value={number}
        onChange={handleNumber}
        placeholder={placeholder || '98765 43210'}
        required={required}
        style={{ borderRadius: '0 8px 8px 0', flex: 1, fontSize: 13, height: 38 }}
      />

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1000,
          background: '#fff', border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          width: 240, maxHeight: 280, overflow: 'hidden',
          display: 'flex', flexDirection: 'column', marginTop: 4,
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <input
              autoFocus
              className="crm-input"
              placeholder="Search country…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12, height: 30, width: '100%' }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map((c, i) => (
              <div
                key={c.iso + i}
                onClick={() => handleSelect(c.code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  background: c.code === dialCode ? 'rgba(124,58,237,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { if (c.code !== dialCode) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                onMouseLeave={e => { if (c.code !== dialCode) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 18 }}>{c.flag}</span>
                <span style={{ flex: 1, color: '#0f172a' }}>{c.name}</span>
                <span style={{ color: '#64748b', fontSize: 12 }}>{c.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
